import { ALERT_MODES } from '../../../utils/alertCreator.utils';
import {
  authorizedAreaRings,
  authorizedJurisdictionFor,
  draftSameCode,
  getFemaJurisdictionBounds,
} from '../../../utils/femaJurisdictions.utils';
import { getIpawsJurisdictionBoundaryRings } from '../../../utils/ipawsArea.utils';

/** The space, in pixels, between the fitted boundary and the map edge. */
export const JURISDICTION_FIT_PADDING = 24;

/**
 * Changes workplace polygons (`[lat, lng]` pairs from `useLocation`) into `{ lat, lng }` rings.
 * @param {Array<Array<[number, number]>>} workplacePolygons The workplace jurisdiction rings.
 * @returns {Array<Array<{ lat: number, lng: number }>>} The same rings as map coordinates.
 */
const toCoordinateRings = (workplacePolygons) =>
  (Array.isArray(workplacePolygons) ? workplacePolygons : []).map((ring) =>
    (Array.isArray(ring) ? ring : []).map(([lat, lng]) => ({ lat, lng })),
  );

/**
 * The fit request for the alert map: a key that names the jurisdiction the map shows, and the
 * bounds of that jurisdiction. The map fits once for each new key (`BoundsSetter`), so a change of
 * the alert type or of the selected jurisdiction moves the map, and a shape edit does not.
 *
 * - RescAlert: the workplace jurisdiction.
 * - IPAWS with a selected area: that area (its SAME code, else the searched place).
 * - IPAWS with no selected area: every area the COG may alert. While that list loads, the request
 *   waits. When the COG holds no area with a boundary, the map shows the workplace jurisdiction.
 *
 * @param {object} options
 * @param {string} options.alertMode `ALERT_MODES.RESCALERT` or `ALERT_MODES.IPAWS`.
 * @param {object|null} options.draft The active alert draft.
 * @param {object[]} options.authorizedJurisdictions `jurisdictions[]` from `/v2/fema/jurisdictions`.
 * @param {boolean} options.isAuthorizedListLoading Whether that list is still loading.
 * @param {Array<Array<[number, number]>>} options.workplacePolygons The workplace rings.
 * @returns {{ key: string, bounds: [[number, number], [number, number]] | undefined }} The request.
 */
export const getAlertMapFitRequest = ({
  alertMode,
  draft,
  authorizedJurisdictions,
  isAuthorizedListLoading,
  workplacePolygons,
}) => {
  const workplaceBounds = getFemaJurisdictionBounds({
    boundaryRings: toCoordinateRings(workplacePolygons),
  });
  if (alertMode !== ALERT_MODES.IPAWS) return { key: 'rescalert', bounds: workplaceBounds };

  const sameCode = draftSameCode(draft);
  const resolvedJurisdiction = draft?.resolvedJurisdiction ?? null;
  if (sameCode || resolvedJurisdiction) {
    const identity =
      sameCode || resolvedJurisdiction.place_id || resolvedJurisdiction.GEOID || 'selected';
    const resolvedRings = resolvedJurisdiction?.boundaryRings;
    const rings = resolvedRings?.length
      ? resolvedRings
      : getIpawsJurisdictionBoundaryRings(
          authorizedJurisdictionFor(authorizedJurisdictions, sameCode),
        );
    return {
      key: `ipaws:${identity}`,
      bounds: getFemaJurisdictionBounds({ boundaryRings: rings }),
    };
  }

  const cogRings = authorizedAreaRings(authorizedJurisdictions, '').flatMap((area) => area.rings);
  if (cogRings.length || isAuthorizedListLoading) {
    return { key: 'ipaws:cog', bounds: getFemaJurisdictionBounds({ boundaryRings: cogRings }) };
  }
  return { key: 'ipaws:workplace', bounds: workplaceBounds };
};
